import {Stack, Typography} from '@mui/material';
import React, {useState} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {translations} from 'presentation/translations-new';
import {FormSessionProps} from './types';
import {renderHiddenIdField, renderServiceDateField, renderSessionValueField} from './utils';
import ModalDatePicker from 'presentation/components/ModalDatePicker';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {isValid as isValidDate} from 'date-fns';
import {parseDate} from 'presentation/utils';
import {
	validateDateFormat,
	validateMinDate,
	validateNotFutureDate,
} from '../../utils/dateValidators';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import type {TherapySessionsData} from '../TherapySessions/types';

const {requiredField} = translations['pt-br'].commons;
const {serviceDate, sessionValue, placeholderDate, placeholderValue} =
	translations['pt-br'].privateFlow.refund.therapies;

const FormSession = ({index, control}: FormSessionProps) => {
	const {formAtoms} = useRefundRequest();
	const {planBeneficiaries} = useAccountInformation();
	const formContext = useFormContext<TherapySessionsData>();
	const [isServiceDateModalOpen, setIsServiceDateModalOpen] = useState(false);

	const patientUserCode = formAtoms.patientDataForm.patient?.userCode;

	const watchedSessions =
		useWatch({
			control,
			name: 'sessions',
		}) || [];
	const serviceDateFormat = watchedSessions?.[index]?.serviceDate;

	const patientCancelled = planBeneficiaries?.find(
		beneficiary => beneficiary?.cdUsuarioC === patientUserCode,
	);

	const handleChangeServiceDate = (date: Date | null) => {
		if (date) {
			const day = String(date.getDate()).padStart(2, '0');
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const year = date.getFullYear();
			const formattedDate = `${day}/${month}/${year}`;
			formContext.setValue(`sessions.${index}.serviceDate`, formattedDate);
			formContext.trigger(`sessions.${index}.serviceDate`);
		} else {
			formContext.setValue(`sessions.${index}.serviceDate`, '');
			formContext.trigger(`sessions.${index}.serviceDate`);
		}
		setIsServiceDateModalOpen(false);
	};
	const getDateFromString = (dateString: string): Date | undefined => {
		if (!dateString) return undefined;
		const parsed = parseDate(dateString);
		return isValidDate(parsed) ? (parsed as Date) : undefined;
	};

	const isPatientCancelled = !!patientCancelled?.dtCancelamentoC;

	const cancellationDate = isPatientCancelled
		? (parseDate(patientCancelled?.dtCancelamentoC) as Date)
		: new Date();

	const handleOpenServiceDateModal = () => {
		setIsServiceDateModalOpen(true);
	};

	const handleClearError = (fieldName: 'serviceDate' | 'sessionValue') => {
		formContext.clearErrors.bind(formContext, `sessions.${index}.${fieldName}`);
		formContext.clearErrors('sessions');
	};

	return (
		<Stack direction="row" spacing={2}>
			<Stack gap={1} flex={1}>
				<Typography color="primary" fontWeight={600}>
					{serviceDate}
				</Typography>
				<Controller
					control={control}
					name={`sessions.${index}.serviceDate`}
					rules={{
						required: requiredField,
						validate: {
							validDate: (value: string | undefined) => {
								const result = validateDateFormat(value);
								return result.isValid || result.message || true;
							},
							notFutureDate: (value: string | undefined) => {
								const result = validateNotFutureDate(value);
								return result.isValid || result.message || true;
							},
							minDate: (value: string | undefined) => {
								const result = validateMinDate(value);
								return result.isValid || result.message || true;
							},
							maxDateBeforeCancellation: (value: string | undefined) => {
								if (!isPatientCancelled || !value) return true;
								const sessionDate = parseDate(value);
								if (!isValidDate(sessionDate)) return true;
								const sessionDateTyped = sessionDate as Date;
								if (sessionDateTyped > cancellationDate) {
									return 'A data da sessão não pode ser posterior à data de cancelamento do contrato';
								}
								return true;
							},
						},
					}}
					render={renderServiceDateField(
						index,
						placeholderDate,
						handleOpenServiceDateModal,
						() => handleClearError('serviceDate'),
					)}
				/>
				<ModalDatePicker
					externalOpen={isServiceDateModalOpen}
					onAbandonModal={() => setIsServiceDateModalOpen(false)}
					selectFirstDate={handleChangeServiceDate}
					currentFirstDate={getDateFromString(serviceDateFormat || '')}
					includeDateIntervals={{
						start: new Date(2000, 0, 1),
						end: cancellationDate,
					}}
					showClearButton
					hideOpenButton
					shouldHaveDateIntervals
				/>
			</Stack>

			<Stack gap={1} flex={1}>
				<Typography color="primary" fontWeight={600}>
					{sessionValue}
				</Typography>
				<Controller
					control={control}
					name={`sessions.${index}.sessionValue`}
					rules={{required: requiredField}}
					render={renderSessionValueField(placeholderValue, () =>
						handleClearError('sessionValue'),
					)}
				/>
			</Stack>

			<Controller
				control={control}
				name={`sessions.${index}.id`}
				render={renderHiddenIdField}
			/>
		</Stack>
	);
};

export {FormSession};
